import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { agentAPI, llmAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Play, Square, Clock, Zap } from 'lucide-react';

export default function AgentConsole() {
  const { agentId } = useParams();
  const { user, refreshUser } = useAuth();
  const [agent, setAgent] = useState(null);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [providers, setProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-5.2');
  const [credits, setCredits] = useState(user?.credits || 0);
  const outputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [agentRes, llmRes, histRes] = await Promise.all([
          agentAPI.get(agentId),
          llmAPI.providers(),
          agentAPI.history(agentId)
        ]);
        setAgent(agentRes.data);
        setProviders(llmRes.data.providers);
        setHistory(histRes.data.history);
      } catch (err) {
        console.error('Console load error:', err);
      }
    };
    load();
  }, [agentId]);

  useEffect(() => {
    if (user) setCredits(user.credits || 0);
  }, [user]);

  const handleExecute = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    setOutput('');
    try {
      const res = await agentAPI.execute(agentId, {
        input_text: input,
        llm_provider: selectedProvider,
        llm_model: selectedModel,
      });
      setOutput(res.data.output);
      setCredits(res.data.remaining_credits);
      refreshUser();
      // Add to local history
      setHistory(prev => [{
        exec_id: res.data.exec_id,
        input_text: input,
        output_text: res.data.output,
        credits_used: res.data.credits_used,
        created_at: new Date().toISOString(),
      }, ...prev]);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Execution failed';
      setOutput(`[ERROR] ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (val) => {
    setSelectedProvider(val);
    const prov = providers[val];
    if (prov) setSelectedModel(prov.default);
  };

  if (!agent) {
    return (
      <div className="min-h-screen bg-[#020202]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="agent-console-page">
      <Navbar />
      <div className="pt-20 px-6 md:px-12 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Link to={`/agents/${agentId}`} className="inline-flex items-center gap-2 text-[#999] hover:text-[#FF7A3D] transition-colors font-mono text-xs" data-testid="back-to-agent">
              <ArrowLeft className="w-4 h-4" />
              BACK TO AGENT
            </Link>
            <div className="flex items-center gap-4">
              <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-xs">
                <Zap className="w-3 h-3 mr-1" /> {credits.toFixed(2)} CREDITS
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Main Console */}
            <div className="lg:col-span-3">
              {/* Terminal Window */}
              <div className="terminal-window rounded-sm overflow-hidden" data-testid="terminal-window">
                <div className="terminal-header">
                  <div className="terminal-dot bg-[#ff003c]" />
                  <div className="terminal-dot bg-[#ffbd2e]" />
                  <div className="terminal-dot bg-[#FF7A3D]" />
                  <span className="font-mono text-xs text-[#999] ml-3">defences.ai://{agent.agent_id}</span>
                  <div className="flex-1" />
                  <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-[10px]">
                    {agent.price_per_run} cr/run
                  </Badge>
                </div>

                {/* LLM Config */}
                <div className="bg-[#0d0d0d] border-b border-[#1a1a1a] p-3 flex flex-wrap gap-3 items-center">
                  <span className="font-mono text-xs text-[#aaa]">LLM:</span>
                  <Select value={selectedProvider} onValueChange={handleProviderChange}>
                    <SelectTrigger className="w-32 h-7 bg-black border-white/10 font-mono text-xs" data-testid="provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      {Object.keys(providers).map(p => (
                        <SelectItem key={p} value={p} className="font-mono text-xs">{p.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="w-48 h-7 bg-black border-white/10 font-mono text-xs" data-testid="model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      {(providers[selectedProvider]?.models || []).map(m => (
                        <SelectItem key={m} value={m} className="font-mono text-xs">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Input */}
                <div className="p-4">
                  <div className="flex items-start gap-2 mb-4">
                    <span className="font-mono text-xs text-[#FF7A3D] mt-2 flex-shrink-0">&gt;_</span>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={agent.input_placeholder || 'Enter your input...'}
                      rows={4}
                      className="flex-1 bg-transparent border-none outline-none resize-none font-mono text-sm text-white placeholder:text-[#555] leading-relaxed"
                      data-testid="agent-input"
                      onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleExecute(); }}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleExecute}
                      disabled={loading || !input.trim()}
                      className="bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest hover:bg-[#FF7A3D]/90 hover:shadow-[0_0_15px_rgba(255,122,61,0.5)] transition-all rounded-sm h-9 px-6 text-xs disabled:opacity-30"
                      data-testid="execute-btn"
                    >
                      {loading ? (
                        <><Square className="w-3 h-3 mr-2 animate-pulse" /> PROCESSING...</>
                      ) : (
                        <><Play className="w-3 h-3 mr-2" /> EXECUTE</>
                      )}
                    </Button>
                    <span className="font-mono text-[10px] text-[#aaa]">CTRL+ENTER to run</span>
                  </div>
                </div>

                {/* Output */}
                {(output || loading) && (
                  <div className="border-t border-[#1a1a1a] p-4" ref={outputRef}>
                    <p className="font-mono text-xs text-[#999] mb-3">
                      <Clock className="w-3 h-3 inline mr-1" /> OUTPUT
                    </p>
                    {loading && !output ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
                        <span className="font-mono text-xs text-[#FF7A3D]">Analyzing...</span>
                      </div>
                    ) : (
                      <pre className="font-mono text-xs text-[#e0e0e0] whitespace-pre-wrap leading-relaxed" data-testid="agent-output">
                        {output}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - History */}
            <div className="card-cyber rounded-sm p-4 h-fit max-h-[600px] overflow-y-auto" data-testid="execution-history">
              <h3 className="font-heading text-sm font-bold text-white mb-4">HISTORY</h3>
              {history.length === 0 ? (
                <p className="font-mono text-xs text-[#aaa]">No executions yet</p>
              ) : (
                <div className="space-y-2">
                  {history.slice(0, 20).map((h) => (
                    <button
                      key={h.exec_id}
                      onClick={() => { setInput(h.input_text); setOutput(h.output_text); }}
                      className="w-full text-left p-3 bg-white/[0.02] border border-white/5 rounded-sm hover:border-[#FF7A3D]/20 transition-colors"
                      data-testid={`history-${h.exec_id}`}
                    >
                      <p className="font-mono text-[10px] text-white truncate">{h.input_text?.substring(0, 40)}...</p>
                      <p className="font-mono text-[10px] text-[#aaa] mt-1">-{h.credits_used}cr</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
